/**
 * Jetons de couleur du design system (§8.1).
 *
 * Définis UNE fois, déclinés en clair et en sombre. Aucun composant ne code une
 * couleur en dur : c'est ce qui permet de changer le thème sans relire l'application.
 */

export const palette = {
  brand: {
    50: '#EEF4FF',
    100: '#D9E6FF',
    200: '#BCD3FF',
    300: '#8EB5FF',
    400: '#598DFF',
    500: '#2F6BFF',
    600: '#1A4FDB',
    700: '#163FB0',
    800: '#17378C',
    900: '#183170',
  },
  neutral: {
    0: '#FFFFFF',
    25: '#FCFCFD',
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
    950: '#020617',
  },
  success: { 50: '#ECFDF5', 500: '#10B981', 700: '#047857' },
  warning: { 50: '#FFFBEB', 500: '#F59E0B', 700: '#B45309' },
  danger: { 50: '#FEF2F2', 500: '#EF4444', 700: '#B91C1C' },
  info: { 50: '#EFF6FF', 500: '#3B82F6', 700: '#1D4ED8' },
} as const

/** Échelle d'espacement, base 4 px. */
export const spacingUnit = 4

export const radius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const

/**
 * Statuts métier. La couleur ne porte JAMAIS l'information seule : chaque
 * statut s'accompagne toujours d'un libellé (accessibilité, §7.12).
 */
export const statusColors = {
  draft: palette.neutral[500],
  validated: palette.info[500],
  paid: palette.success[500],
  partially_paid: palette.warning[500],
  overdue: palette.danger[500],
  cancelled: palette.neutral[400],
} as const

/**
 * État de synchronisation (§8.1).
 * ⚠️ « Hors ligne » est ambre, pas rouge : c'est un état normal et maîtrisé.
 * Le rouge reste réservé à ce qui exige une action de l'utilisateur.
 */
export const syncColors = {
  synced: palette.success[500],
  syncing: palette.info[500],
  offline: palette.warning[500],
  // Rouge, comme un conflit : les deux exigent une action de l'utilisateur.
  blocked: palette.danger[500],
  conflict: palette.danger[500],
} as const

export type ThemeMode = 'light' | 'dark'
